/**
 * Contacts API Service - People & Companies CRM
 *
 * Provides typed API client for CRM operations:
 * - Contact CRUD operations
 * - Company management
 * - Contact search and resolution
 */

import axios from 'axios';

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('ngui_auth_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== Types ====================

export interface ContactEmail {
  id: string;
  email: string;
  email_type: 'work' | 'personal' | 'other';
  is_primary: boolean;
  label?: string;
}

export interface ContactPhone {
  id: string;
  phone: string;
  phone_type: 'work' | 'mobile' | 'home' | 'other';
  is_primary: boolean;
  label?: string;
}

export interface ContactCompany {
  role_id: string;
  company_id: string;
  company_name: string;
  title?: string;
  department?: string;
  role_type: string;
  is_current: boolean;
  is_primary: boolean;
  start_date?: string;
  end_date?: string;
}

export interface Contact {
  id: string;
  first_name: string;
  last_name?: string;
  full_name: string;
  aliases: string[];
  primary_email?: string;
  primary_phone?: string;
  title?: string;
  department?: string;
  avatar_url?: string;
  notes?: string;
  timezone?: string;
  location?: string;
  linkedin_url?: string;
  twitter_handle?: string;
  github_username?: string;
  importance: 'vip' | 'high' | 'normal' | 'low';
  tags: string[];
  last_contacted_at?: string;
  created_at: string;
  updated_at: string;
  emails: ContactEmail[];
  phones: ContactPhone[];
  companies: ContactCompany[];
  // Relationship fields
  relationship_to_user?: string;
  short_bio?: string;
  relationship_goal?: string;
  is_blacklisted?: boolean;
  blacklist_reason?: string;
  blacklisted_at?: string;
}

export interface ContactListItem {
  id: string;
  first_name: string;
  last_name?: string;
  full_name: string;
  primary_email?: string;
  primary_phone?: string;
  title?: string;
  importance: string;
  avatar_url?: string;
  last_contacted_at?: string;
  companies: ContactCompany[];
}

export interface ContactListResponse {
  contacts: ContactListItem[];
  total_count: number;
  total_pages: number;
  page: number;
  limit: number;
}

export interface CompanyContact {
  role_id: string;
  contact_id: string;
  full_name: string;
  title?: string;
  department?: string;
  role_type: string;
  is_primary: boolean;
  is_current: boolean;
  start_date?: string;
  end_date?: string;
  email?: string;
  avatar_url?: string;
  importance?: string;
}

export interface CompanyEmail {
  id: string;
  email: string;
  email_type: string;
  label?: string;
  is_primary: boolean;
}

export interface CompanyPhone {
  id: string;
  phone: string;
  phone_type: string;
  label?: string;
  is_primary: boolean;
}

export interface CompanyAddress {
  id: string;
  address_type: string;
  label?: string;
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary: boolean;
}

export interface CompanyEmailInput {
  email: string;
  email_type?: string;
  label?: string;
  is_primary?: boolean;
}

export interface CompanyPhoneInput {
  phone: string;
  phone_type?: string;
  label?: string;
  is_primary?: boolean;
}

export interface CompanyAddressInput {
  address_type?: string;
  label?: string;
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary?: boolean;
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  aliases: string[];
  industry?: string;
  size?: string;
  website?: string;
  linkedin_url?: string;
  description?: string;
  logo_url?: string;
  tags: string[];
  contact_count: number;
  contacts?: CompanyContact[];  // Contacts at this company (from company_get)
  emails?: CompanyEmail[];      // Company emails (from company_get)
  phones?: CompanyPhone[];      // Company phones (from company_get)
  addresses?: CompanyAddress[]; // Company addresses (from company_get)
  created_at: string;
  updated_at: string;
  // Relationship fields
  relationship_to_user?: string;
  short_bio?: string;
  relationship_goal?: string;
  is_blacklisted?: boolean;
  blacklist_reason?: string;
  blacklisted_at?: string;
}

export interface CompanyListResponse {
  companies: Company[];
  total_count: number;
  total_pages: number;
  page: number;
  limit: number;
}

// ==================== Create/Update Requests ====================

export interface ContactEmailInput {
  email: string;
  email_type?: string;
  is_primary?: boolean;
  label?: string;
}

export interface ContactPhoneInput {
  phone: string;
  phone_type?: string;
  is_primary?: boolean;
  label?: string;
}

export interface ContactRoleInput {
  company_id: string;
  title?: string;
  department?: string;
  role_type?: string;
  is_current?: boolean;
  is_primary?: boolean;
}

export interface ContactRoleUpdateInput {
  title?: string;
  department?: string;
  role_type?: string;
  is_current?: boolean;
  is_primary?: boolean;
  start_date?: string;
  end_date?: string;
  work_email?: string;
}

export interface ContactRoleResponse {
  role_id: string;
  contact_id: string;
  company_id: string;
  company_name: string;
  title?: string;
  department?: string;
  role_type: string;
  is_current: boolean;
  is_primary: boolean;
  start_date?: string;
  end_date?: string;
  work_email?: string;
}

export interface CreateContactRequest {
  first_name: string;
  last_name?: string;
  aliases?: string[];
  primary_email?: string;
  primary_phone?: string;
  title?: string;
  department?: string;
  avatar_url?: string;
  notes?: string;
  timezone?: string;
  location?: string;
  linkedin_url?: string;
  twitter_handle?: string;
  github_username?: string;
  importance?: 'vip' | 'high' | 'normal' | 'low';
  tags?: string[];
  emails?: ContactEmailInput[];
  phones?: ContactPhoneInput[];
  company_roles?: ContactRoleInput[];
}

export interface UpdateContactRequest {
  first_name?: string | null;
  last_name?: string | null;
  aliases?: string[];
  primary_email?: string | null;
  primary_phone?: string | null;
  title?: string | null;
  department?: string | null;
  avatar_url?: string | null;
  notes?: string | null;
  timezone?: string | null;
  location?: string | null;
  linkedin_url?: string | null;
  twitter_handle?: string | null;
  github_username?: string | null;
  importance?: 'vip' | 'high' | 'normal' | 'low';
  tags?: string[];
  // Relationship fields
  relationship_to_user?: string | null;
  short_bio?: string | null;
  relationship_goal?: string | null;
  is_blacklisted?: boolean;
  blacklist_reason?: string | null;
}

export interface CreateCompanyRequest {
  name: string;
  domain?: string;
  aliases?: string[];
  industry?: string;
  size?: string;
  website?: string;
  linkedin_url?: string;
  description?: string;
  logo_url?: string;
  tags?: string[];
}

export interface UpdateCompanyRequest {
  name?: string | null;
  domain?: string | null;
  aliases?: string[];
  industry?: string | null;
  size?: string | null;
  website?: string | null;
  linkedin_url?: string | null;
  description?: string | null;
  logo_url?: string | null;
  tags?: string[];
  // Relationship fields
  relationship_to_user?: string | null;
  short_bio?: string | null;
  relationship_goal?: string | null;
  is_blacklisted?: boolean;
  blacklist_reason?: string | null;
}

// ==================== Relation Type Constants ====================

export const CONTACT_RELATION_TYPES = [
  { value: 'boss', label: 'Boss/Manager' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'subordinate', label: 'Subordinate' },
  { value: 'client', label: 'Client' },
  { value: 'vendor', label: 'Vendor/Supplier' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'friend', label: 'Friend' },
  { value: 'investor', label: 'Investor' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'unknown', label: 'Not Classified' },
] as const;

export const COMPANY_RELATION_TYPES = [
  { value: 'employer', label: 'Employer (we work for them)' },
  { value: 'customer', label: 'Customer (they pay us)' },
  { value: 'supplier', label: 'Supplier (we pay them)' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'partner', label: 'Partner' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'investor', label: 'Investor' },
  { value: 'portfolio', label: 'Portfolio Company' },
  { value: 'unknown', label: 'Not Classified' },
] as const;

// ==================== AI Review Types ====================

export type ReviewStatus = 'none' | 'pending' | 'reviewed' | 'merged';
export type ReviewFilter = 'all' | 'pending' | 'auto_merged' | 'reviewed';

export interface PendingReviewContact {
  id: string;
  first_name: string;
  last_name?: string;
  full_name: string;
  primary_email?: string;
  review_status: ReviewStatus;
  ai_confidence: number;
  ai_reasoning?: string;
  email_source_summary_id?: string;
  candidate_match_id?: string;
  candidate_match_name?: string;
  candidate_match_email?: string;
  created_at: string;
}

export interface PendingReviewCompany {
  id: string;
  name: string;
  domain?: string;
  review_status: ReviewStatus;
  ai_confidence: number;
  ai_reasoning?: string;
  email_source_summary_id?: string;
  candidate_match_id?: string;
  candidate_match_name?: string;
  is_subsidiary?: boolean;
  parent_company_id?: string;
  parent_company_name?: string;
  created_at: string;
}

export interface MergeLogEntry {
  id: string;
  source_id: string;
  target_id: string;
  source_name: string;
  target_name: string;
  ai_confidence: number;
  ai_reasoning?: string;
  merged_by?: string;
  merged_at: string;
  can_undo: boolean;
}

export interface ReviewStats {
  total_extracted: number;
  auto_merged: number;
  pending_review: number;
  reviewed: number;
}

export interface PendingReviewResponse {
  items: PendingReviewContact[] | PendingReviewCompany[];
  stats: ReviewStats;
  total_count: number;
  page: number;
  limit: number;
}

export interface MergeLogResponse {
  entries: MergeLogEntry[];
  total_count: number;
  page: number;
  limit: number;
}

// ==================== Manual Merge Types ====================

export interface MergeCandidate {
  id: string;
  name: string;
  email?: string;
  domain?: string;
  similarity_score: number;
  match_reason: string;
  contact_count?: number;  // For companies
  avatar_url?: string;     // For contacts
  title?: string;          // For contacts
}

export interface MergeCandidatesResponse {
  candidates: MergeCandidate[];
  source: {
    id: string;
    name: string;
    email?: string;
    domain?: string;
  };
}

export interface ManualMergeResult {
  success: boolean;
  merged_entity: Contact | Company;
  message: string;
}

// ==================== Communication (Email Threads) Types ====================

export interface EmailThreadParticipant {
  email: string;
  name?: string;
}

export interface EmailThread {
  id: string;
  subject?: string;
  participants: EmailThreadParticipant[];
  message_count: number;
  unread_count: number;
  has_attachments: boolean;
  is_starred: boolean;
  first_message_at?: string;
  last_message_at: string;
  snippet?: string;
  sender_name?: string;
  sender_email?: string;
  contact_sent_count?: number;
  user_sent_count?: number;
}

export interface CommunicationResponse {
  threads: EmailThread[];
  total_count: number;
  total_pages: number;
  page: number;
  limit: number;
  contact_id?: string;
  company_id?: string;
  email_addresses: string[];
}

// ==================== Email Insights Types ====================

export interface ContactEmailInsights {
  contact_id: string;
  total_emails: number;
  // User-centric labels (what the user experiences)
  received_from_contact: number;  // Contact SENT = user RECEIVED
  sent_to_contact: number;        // Contact RECEIVED = user SENT
  // Legacy fields (kept for backward compatibility)
  emails_received: number;
  emails_sent: number;
  // Contact profile fields
  title?: string;
  primary_phone?: string;
  current_role?: {
    title?: string;
    company: string;
    company_id: string;
  };
  first_contact_at?: string;
  last_contact_at?: string;
  avg_response_time_hours?: number;
  ai_confidence?: number;
  ai_reasoning?: string;
  review_status: ReviewStatus;
  candidate_match?: {
    id: string;
    name: string;
    email?: string;
    confidence: number;
    reasoning: string;
  };
  topics: string[];
  expertise: string[];
  facts: string[];
  location?: string;
  related_people: Array<{
    id: string;
    name: string;
    email?: string;
    email_count: number;
  }>;
  related_companies: Array<{
    id: string;
    name: string;
    domain?: string;
    contact_count: number;  // Number of contacts at this company
  }>;
  source_email_ids: string[];
  last_updated_at?: string;
}

export interface CompanyEmailInsights {
  company_id: string;
  total_emails: number;
  known_domains: string[];
  first_contact_at?: string;
  last_contact_at?: string;
  ai_confidence?: number;
  ai_reasoning?: string;
  review_status: ReviewStatus;
  is_subsidiary?: boolean;
  parent_company?: {
    id: string;
    name: string;
  };
  candidate_match?: {
    id: string;
    name: string;
    domain?: string;
    confidence: number;
    reasoning: string;
  };
  key_contacts: Array<{
    id: string;
    name: string;
    title?: string;
    email?: string;
    email_count: number;
  }>;
  topics: string[];
  relationship_health?: number;
  sentiment_trend?: 'improving' | 'stable' | 'declining';
  source_email_ids: string[];
  last_updated_at?: string;
}

// ==================== Knowledge Card Types ====================

export type KnowledgeCardType = 'insight' | 'topic' | 'relationship' | 'note' | 'fact' | 'suppression';
export type KnowledgeCardSource = 'ai_generated' | 'manual' | 'email_extracted';

export interface KnowledgeCard {
  id: string;
  contact_id?: string;
  company_id?: string;
  card_type: KnowledgeCardType;
  source: KnowledgeCardSource;
  title?: string;
  content: string;
  confidence?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: number;
}

export interface CreateKnowledgeCardRequest {
  card_type: KnowledgeCardType;
  title?: string;
  content: string;
}

// ==================== API Functions ====================

const contactsApi = {
  /**
   * List contacts with pagination
   */
  list: async (
    page: number = 1,
    limit: number = 50,
    importance?: string,
    companyId?: string
  ): Promise<ContactListResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (importance) params.append('importance', importance);
    if (companyId) params.append('company_id', companyId);
    const response = await apiClient.get(`/contacts?${params}`);
    return response.data;
  },

  /**
   * Get a single contact with full details
   */
  get: async (contactId: string): Promise<Contact> => {
    const response = await apiClient.get(`/contacts/${contactId}`);
    return response.data;
  },

  /**
   * Create a new contact
   */
  create: async (data: CreateContactRequest): Promise<Contact> => {
    const response = await apiClient.post('/contacts', data);
    return response.data;
  },

  /**
   * Update a contact
   */
  update: async (contactId: string, data: UpdateContactRequest): Promise<Contact> => {
    const response = await apiClient.patch(`/contacts/${contactId}`, data);
    return response.data;
  },

  /**
   * Delete a contact
   */
  delete: async (contactId: string): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}`);
  },

  /**
   * Search contacts (basic fulltext search)
   */
  search: async (query: string, limit: number = 20): Promise<ContactListItem[]> => {
    const response = await apiClient.get(`/contacts/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },

  /**
   * Semantic search for contacts using natural language queries.
   *
   * Supports queries like "Vitaly from MyCloudGrocer" or "e-commerce expert in Brooklyn"
   * by combining full-text search with embeddings-based semantic search.
   *
   * @param query - Natural language search query
   * @param options - Search options
   * @param options.limit - Maximum number of results (default: 20)
   * @param options.mode - Search mode: 'semantic' | 'fulltext' | 'hybrid' (default: 'hybrid')
   * @param options.includeCompanies - Include companies in results (default: false)
   */
  searchSemantic: async (
    query: string,
    options: {
      limit?: number;
      mode?: 'semantic' | 'fulltext' | 'hybrid';
      includeCompanies?: boolean;
    } = {}
  ): Promise<{
    results: Array<{
      id: string;
      full_name?: string;
      name?: string;
      primary_email?: string;
      email?: string;
      title?: string;
      company?: string;
      avatar_url?: string;
      importance?: string;
      entity_type: 'contact' | 'company';
      hybrid_score?: number;
      similarity?: number;
      match_type?: string;
    }>;
    total: number;
    query: string;
    mode: string;
  }> => {
    const params = new URLSearchParams({
      q: query,
      limit: String(options.limit ?? 20),
      mode: options.mode ?? 'hybrid',
      include_companies: String(options.includeCompanies ?? false),
    });
    const response = await apiClient.get(`/contacts/search/semantic?${params}`);
    return response.data;
  },

  /**
   * Add email to contact
   */
  addEmail: async (contactId: string, email: ContactEmailInput): Promise<ContactEmail> => {
    const response = await apiClient.post(`/contacts/${contactId}/emails`, email);
    return response.data;
  },

  /**
   * Add phone to contact
   */
  addPhone: async (contactId: string, phone: ContactPhoneInput): Promise<ContactPhone> => {
    const response = await apiClient.post(`/contacts/${contactId}/phones`, phone);
    return response.data;
  },

  /**
   * Delete email from contact
   */
  deleteEmail: async (contactId: string, emailId: string): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}/emails/${emailId}`);
  },

  /**
   * Delete phone from contact
   */
  deletePhone: async (contactId: string, phoneId: string): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}/phones/${phoneId}`);
  },

  /**
   * Add role (company association) to contact
   */
  addRole: async (contactId: string, role: ContactRoleInput): Promise<ContactCompany> => {
    const response = await apiClient.post(`/contacts/${contactId}/roles`, role);
    return response.data;
  },

  /**
   * Delete role (company association) from contact
   */
  deleteRole: async (contactId: string, companyId: string): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}/roles/${companyId}`);
  },

  /**
   * Update a contact role at a company (by role ID)
   */
  updateRole: async (roleId: string, updates: ContactRoleUpdateInput): Promise<ContactRoleResponse> => {
    const response = await apiClient.patch(`/contacts/roles/${roleId}`, updates);
    return response.data;
  },

  /**
   * Delete a contact role by role ID (remove person from company)
   */
  deleteRoleById: async (roleId: string): Promise<void> => {
    await apiClient.delete(`/contacts/roles/${roleId}`);
  },

  // ==================== AI Review Operations ====================

  /**
   * Get contacts pending review (AI-extracted with medium confidence)
   */
  getPendingReview: async (
    filter: ReviewFilter = 'all',
    page: number = 1,
    limit: number = 50
  ): Promise<PendingReviewResponse> => {
    const params = new URLSearchParams({ filter, page: String(page), limit: String(limit) });
    const response = await apiClient.get(`/contacts/pending-review?${params}`);
    return response.data;
  },

  /**
   * Get contact merge log (recent auto-merges)
   */
  getMergeLog: async (page: number = 1, limit: number = 50): Promise<MergeLogResponse> => {
    const response = await apiClient.get(`/contacts/merge-log?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Approve AI merge decision for a contact
   */
  approveMerge: async (contactId: string, targetId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/${contactId}/approve-merge`, { target_id: targetId });
    return response.data;
  },

  /**
   * Reject AI merge - keep contact separate
   */
  rejectMerge: async (contactId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/${contactId}/reject-merge`);
    return response.data;
  },

  /**
   * Undo a recent auto-merge
   */
  undoMerge: async (mergeLogId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/merge-log/${mergeLogId}/undo`);
    return response.data;
  },

  /**
   * Trigger CRM vacuum sync (manual)
   */
  triggerSync: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/contacts/trigger-vacuum');
    return response.data;
  },

  // ==================== Email Insights ====================

  /**
   * Get email-derived insights for a contact
   */
  getEmailInsights: async (contactId: string): Promise<ContactEmailInsights> => {
    const response = await apiClient.get(`/contacts/${contactId}/email-insights`);
    return response.data;
  },

  /**
   * Refresh AI insights for a contact
   */
  refreshInsights: async (contactId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/${contactId}/refresh-insights`);
    return response.data;
  },

  // ==================== Knowledge Cards ====================

  /**
   * Get knowledge cards for a contact
   */
  getKnowledgeCards: async (contactId: string): Promise<KnowledgeCard[]> => {
    const response = await apiClient.get(`/contacts/${contactId}/knowledge`);
    return response.data;
  },

  /**
   * Add a manual note (knowledge card) to a contact
   */
  addNote: async (contactId: string, data: CreateKnowledgeCardRequest): Promise<KnowledgeCard> => {
    const response = await apiClient.post(`/contacts/${contactId}/knowledge`, data);
    return response.data;
  },

  /**
   * Delete a knowledge card
   */
  deleteKnowledgeCard: async (contactId: string, cardId: string): Promise<void> => {
    await apiClient.delete(`/contacts/${contactId}/knowledge/${cardId}`);
  },

  // ==================== Manual Merge Operations ====================

  /**
   * Get merge candidates for a contact (potential duplicates)
   */
  getMergeCandidates: async (contactId: string): Promise<MergeCandidatesResponse> => {
    const response = await apiClient.get(`/contacts/${contactId}/merge-candidates`);
    return response.data;
  },

  /**
   * Manually merge a contact into another (source → target)
   * The source contact will be soft-deleted after merging
   */
  manualMerge: async (sourceContactId: string, targetContactId: string): Promise<ManualMergeResult> => {
    const response = await apiClient.post(`/contacts/${sourceContactId}/manual-merge`, {
      target_id: targetContactId,
    });
    return response.data;
  },

  // ==================== Communication (Email Threads) ====================

  /**
   * Get email threads involving a contact
   */
  getCommunication: async (contactId: string, page: number = 1, limit: number = 50): Promise<CommunicationResponse> => {
    const response = await apiClient.get(`/contacts/${contactId}/communication?page=${page}&limit=${limit}`);
    return response.data;
  },
};

const companiesApi = {
  /**
   * List companies with pagination
   */
  list: async (page: number = 1, limit: number = 50): Promise<CompanyListResponse> => {
    const response = await apiClient.get(`/contacts/companies?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Get a single company
   */
  get: async (companyId: string): Promise<Company> => {
    const response = await apiClient.get(`/contacts/companies/${companyId}`);
    return response.data;
  },

  /**
   * Create a new company
   */
  create: async (data: CreateCompanyRequest): Promise<Company> => {
    const response = await apiClient.post('/contacts/companies', data);
    return response.data;
  },

  /**
   * Update a company
   */
  update: async (companyId: string, data: UpdateCompanyRequest): Promise<Company> => {
    const response = await apiClient.patch(`/contacts/companies/${companyId}`, data);
    return response.data;
  },

  /**
   * Delete a company
   */
  delete: async (companyId: string): Promise<void> => {
    await apiClient.delete(`/contacts/companies/${companyId}`);
  },

  // ==================== AI Review Operations ====================

  /**
   * Get companies pending review (AI-extracted with medium confidence)
   */
  getPendingReview: async (
    filter: ReviewFilter = 'all',
    page: number = 1,
    limit: number = 50
  ): Promise<PendingReviewResponse> => {
    const params = new URLSearchParams({ filter, page: String(page), limit: String(limit) });
    const response = await apiClient.get(`/contacts/companies/pending-review?${params}`);
    return response.data;
  },

  /**
   * Get company merge log (recent auto-merges)
   */
  getMergeLog: async (page: number = 1, limit: number = 50): Promise<MergeLogResponse> => {
    const response = await apiClient.get(`/contacts/companies/merge-log?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Approve AI merge decision for a company
   */
  approveMerge: async (companyId: string, targetId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/approve-merge`, { target_id: targetId });
    return response.data;
  },

  /**
   * Reject AI merge - keep company separate
   */
  rejectMerge: async (companyId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/reject-merge`);
    return response.data;
  },

  /**
   * Mark company as subsidiary of another
   */
  markSubsidiary: async (companyId: string, parentId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/mark-subsidiary`, { parent_id: parentId });
    return response.data;
  },

  /**
   * Undo a recent company merge
   */
  undoMerge: async (mergeLogId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/companies/merge-log/${mergeLogId}/undo`);
    return response.data;
  },

  // ==================== Email Insights ====================

  /**
   * Get email-derived insights for a company
   */
  getEmailInsights: async (companyId: string): Promise<CompanyEmailInsights> => {
    const response = await apiClient.get(`/contacts/companies/${companyId}/email-insights`);
    return response.data;
  },

  /**
   * Refresh AI insights for a company
   */
  refreshInsights: async (companyId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/refresh-insights`);
    return response.data;
  },

  // ==================== Knowledge Cards ====================

  /**
   * Get knowledge cards for a company
   */
  getKnowledgeCards: async (companyId: string): Promise<KnowledgeCard[]> => {
    const response = await apiClient.get(`/contacts/companies/${companyId}/knowledge`);
    return response.data;
  },

  /**
   * Add a manual note (knowledge card) to a company
   */
  addNote: async (companyId: string, data: CreateKnowledgeCardRequest): Promise<KnowledgeCard> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/knowledge`, data);
    return response.data;
  },

  /**
   * Delete a knowledge card from a company
   */
  deleteKnowledgeCard: async (companyId: string, cardId: string): Promise<void> => {
    await apiClient.delete(`/contacts/companies/${companyId}/knowledge/${cardId}`);
  },

  // ==================== Company Emails ====================

  /**
   * Add an email to a company
   */
  addEmail: async (companyId: string, data: CompanyEmailInput): Promise<CompanyEmail> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/emails`, data);
    return response.data;
  },

  /**
   * Delete an email from a company
   */
  deleteEmail: async (companyId: string, emailId: string): Promise<void> => {
    await apiClient.delete(`/contacts/companies/${companyId}/emails/${emailId}`);
  },

  // ==================== Company Phones ====================

  /**
   * Add a phone to a company
   */
  addPhone: async (companyId: string, data: CompanyPhoneInput): Promise<CompanyPhone> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/phones`, data);
    return response.data;
  },

  /**
   * Delete a phone from a company
   */
  deletePhone: async (companyId: string, phoneId: string): Promise<void> => {
    await apiClient.delete(`/contacts/companies/${companyId}/phones/${phoneId}`);
  },

  // ==================== Company Addresses ====================

  /**
   * Add an address to a company
   */
  addAddress: async (companyId: string, data: CompanyAddressInput): Promise<CompanyAddress> => {
    const response = await apiClient.post(`/contacts/companies/${companyId}/addresses`, data);
    return response.data;
  },

  /**
   * Update an address for a company
   */
  updateAddress: async (companyId: string, addressId: string, data: CompanyAddressInput): Promise<CompanyAddress> => {
    const response = await apiClient.patch(`/contacts/companies/${companyId}/addresses/${addressId}`, data);
    return response.data;
  },

  /**
   * Delete an address from a company
   */
  deleteAddress: async (companyId: string, addressId: string): Promise<void> => {
    await apiClient.delete(`/contacts/companies/${companyId}/addresses/${addressId}`);
  },

  // ==================== Manual Merge Operations ====================

  /**
   * Get merge candidates for a company (potential duplicates)
   */
  getMergeCandidates: async (companyId: string): Promise<MergeCandidatesResponse> => {
    const response = await apiClient.get(`/contacts/companies/${companyId}/merge-candidates`);
    return response.data;
  },

  /**
   * Manually merge a company into another (source → target)
   * The source company will be soft-deleted after merging
   */
  manualMerge: async (sourceCompanyId: string, targetCompanyId: string): Promise<ManualMergeResult> => {
    const response = await apiClient.post(`/contacts/companies/${sourceCompanyId}/manual-merge`, {
      target_id: targetCompanyId,
    });
    return response.data;
  },

  // ==================== Communication (Email Threads) ====================

  /**
   * Get email threads involving a company
   */
  getCommunication: async (companyId: string, page: number = 1, limit: number = 50): Promise<CommunicationResponse> => {
    const response = await apiClient.get(`/contacts/companies/${companyId}/communication?page=${page}&limit=${limit}`);
    return response.data;
  },
};

export { contactsApi, companiesApi };
export default contactsApi;
